import {
  addToast,
  Button,
  ColumnTable,
  Icon,
  OutlinedSelect,
  Table,
} from '@octano/global-ui';
import dayjs from 'dayjs';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { useActionKitImp } from '../../../../components/ozmoit/common/action-kit-imp';
import { TranslationsKeys } from '../../../../locales/translations';
import { downloadFromBlob } from '../../../../utils/blob';
import { exportDetailsAction } from '../../actions';
import { useWarningsSummaryDetailsFormLoaderData } from '../../loaders/StudentWarningsSummaryDetailsFormLoader';
import { useWarningsSummaryDetailsLoader } from '../../loaders/StudentWarningsSummaryDetailsLoader';
import BehaviorWarningGroupModal, {
  BehaviorWarningGroupModalMethods,
  BehaviorWarningGroupRecord,
} from './BehaviorWarningGroupModal';

const BehaviorWarningGroup = () => {
  const data = useWarningsSummaryDetailsFormLoaderData();
  const { result, query, refresh } = useWarningsSummaryDetailsLoader();
  const { t } = useTranslation(TranslationsKeys.STUDENT_ALERTS_DETAILS);
  const modalRef = useRef<BehaviorWarningGroupModalMethods>(null);

  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [severityFilter, setSeverityFilter] = useState<string | null>(null);

  const { action: handleExport, loading: exporting } = useActionKitImp(
    exportDetailsAction,
    {
      onSuccess: () => {
        addToast({
          color: 'success',
          text: t(`group.behavior.export.success`),
        });
      },
      onError: {
        unknown: () => {
          addToast({
            color: 'danger',
            text: t(`group.behavior.export.failed`),
          });
        },
      },
    },
  );

  const items = useMemo(() => {
    if (!result?.success) {
      return [];
    }

    return (result.data?.groupData as BehaviorWarningGroupRecord[]) ?? [];
  }, [result]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (typeFilter && item.type !== typeFilter) {
        return false;
      }
      if (severityFilter && item.severity !== severityFilter) {
        return false;
      }
      return true;
    });
  }, [items, typeFilter, severityFilter]);

  const handleOpenModal = useCallback(
    (record?: BehaviorWarningGroupRecord) => {
      if (exporting) {
        return;
      }
      modalRef.current?.open(record);
    },
    [exporting],
  );

  const columns = useMemo<ColumnTable<BehaviorWarningGroupRecord>[]>(
    () => [
      {
        columnName: 'type',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.behavior.table.type`),
        cellFormat: ({ value }) => {
          const type = data?.behaviorRecordTypes.find(
            (option) => option.value === value,
          );
          return type ? type.label : value;
        },
      },
      {
        columnName: 'date',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.behavior.table.date`),
        cellFormat: ({ value }) => {
          const date = dayjs(value);
          return date.utc().format('DD/MM/YYYY');
        },
      },
      {
        columnName: 'title',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.behavior.table.title`),
      },
      {
        columnName: 'notes',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.behavior.table.notes`),
      },
      {
        columnName: 'severity',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.behavior.table.severity`),
        cellFormat: ({ value }) => {
          const severity = data?.behaviorRecordSeverities.find(
            (option) => option.value === value,
          );
          return severity ? severity.label : '-';
        },
      },
      {
        columnName: 'penalization',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.behavior.table.penalization`),
        cellFormat: ({ value }) => {
          const penalization = data?.behaviorRecordPenalizations.find(
            (option) => option.value === value,
          );
          return penalization ? penalization.label : '-';
        },
      },
      {
        columnName: 'conditionality',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.behavior.table.conditionality`),
        cellFormat: ({ value }) => {
          const conditionality = data?.behaviorRecordConditionalities.find(
            (option) => option.value === value,
          );
          return conditionality ? conditionality.label : '-';
        },
      },
      {
        columnName: 'detentionDays',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.behavior.table.detentionDays`),
        cellFormat: ({ value }) => {
          return value ?? '-';
        },
      },
      {
        columnName: 'id',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.behavior.table.details`),
        cellFormat: ({ row }) => {
          return (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => handleOpenModal(row)}
            >
              <Icon name="eye" color={exporting ? 'disabled' : 'primary'} />
            </button>
          );
        },
      },
    ],
    [t, data, handleOpenModal, exporting],
  );

  const handlePressExport = useCallback(async () => {
    if (exporting) {
      return;
    }
    const response = await handleExport(query);
    if (!response.success) {
      return;
    }

    const blob = new Blob([response.data], {
      type: 'vnd.ms-excel',
    });
    downloadFromBlob(blob, `${t(`group.behavior.export.title`)}.xlsx`);
  }, [handleExport, query, exporting, t]);

  return (
    <div className="w-100 d-flex flex-column mt-3" style={{ gap: 12 }}>
      <Row>
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="typeFilter"
            label={t(`group.behavior.table.type`)}
            options={data?.behaviorRecordTypes || []}
            isClearable
            value={
              data?.behaviorRecordTypes.find(
                (option) => option.value === typeFilter,
              ) || undefined
            }
            onChange={(option) => setTypeFilter(option ? option.value : null)}
            disabled={!data?.behaviorRecordTypes.length || exporting}
          />
        </Col>
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="severityFilter"
            label={t(`group.behavior.table.severity`)}
            options={data?.behaviorRecordSeverities || []}
            isClearable
            value={
              data?.behaviorRecordSeverities.find(
                (option) => option.value === severityFilter,
              ) || undefined
            }
            onChange={(option) =>
              setSeverityFilter(option ? option.value : null)
            }
            disabled={!data?.behaviorRecordSeverities.length || exporting}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={6}>
          <h2 className="text-primary fw-700 fs-20 mb-0 mt-3">
            {t(`group.behavior.title`)}
          </h2>
        </Col>
        <Col xs={12} md={6}>
          <div className="d-flex justify-content-end">
            <Button
              text={t(`group.behavior.actions.downloadReport`)}
              size="md"
              type="button"
              icon="download"
              loading={exporting}
              onClick={handlePressExport}
              disabled={!items.length}
            />
            <Button
              text={t(`group.behavior.actions.addRecord`)}
              className="ml-2"
              size="md"
              outlined
              type="button"
              icon="plus"
              onClick={() => handleOpenModal()}
            />
          </div>
        </Col>
      </Row>
      <Table data={filteredItems} columns={columns} />
      <BehaviorWarningGroupModal ref={modalRef} onSave={refresh} />
    </div>
  );
};

export default BehaviorWarningGroup;
